import React, {useEffect} from 'react';
import {useNavigate} from 'react-router-dom';

import {PageHeadline} from 'src/components/page-headline/page-headline';
import {BUTTON_TYPE, RemoveChangeRequest} from 'src/components/remove-change-request/remove-change-request';
import {useUserEmail} from 'src/hooks/useUserEmail';
import {useAiTour} from 'src/modules/ai';
import {EventCreateReviewController} from 'src/modules/ai/change-request-review/event-create-review/event-create-review.controller';
import {
    ChangeRequestSubscriptionDropdown,
} from 'src/modules/change-requests/subscription/change-request-subscription.dropdown';
import {createEventRoutePath} from 'src/modules/event/event-route-planner';
import {getChangeRequestInfo} from 'src/modules/event-create/services';
import {CreationAttribute, UpdateChangeRequest, UpdateChangeRequestType} from 'src/modules/event-create/typedefs';
import {ChangeRequestContextualLink} from 'src/openapi/models/ChangeRequestContextualLink';
import {DecorationType} from 'src/openapi/models/DecorationType';
import {EventChangeRequest} from 'src/openapi/models/EventChangeRequest';
import {useAppSelector} from 'src/store';

import Button from '@pb/kit-component-button';
import {Group, Item} from '@pb/kit-component-group';
import {
    HorizontalPositionElement,
    PushNotification,
    VerticalPositionElement,
} from '@pb/kit-component-push-notification';

import {AttributesBlockController} from './attributes-block/attributes-block.controller';
import {EVENT_MODE_TITLE, EventCreationMode} from './constants';
import {HeaderBlockController} from './header-block/header-block.controller';

type Props = {
    mode: EventCreationMode,
    validationHighLight: boolean,
    changeRequestId: number | null,
    defaultDueDate: Date | undefined,
    decorationTemplate: CreationAttribute[],
    template: CreationAttribute[],
    isLoadingAttributes: boolean,
    isEventMarkedDeprecated: boolean,
    updateEventInfo: EventChangeRequest,
    eventChangeRequest: EventChangeRequest,
    onChangeContextualLinks: (value: ChangeRequestContextualLink[]) => void,
    onUpdateChangeRequest: (value: UpdateChangeRequest, type: UpdateChangeRequestType) => void,
    onChangeDueDate: (date?: Date) => void,
    onChangeDecorationType: (value: DecorationType | null) => void,
    onSave: () => void,
    onSendToReview: () => Promise<void>,
};

export const EventCreateView = (props: Props) => {
    const {
        mode,
        validationHighLight,
        changeRequestId,
        defaultDueDate,
        decorationTemplate,
        template,
        isLoadingAttributes,
        isEventMarkedDeprecated,
        updateEventInfo,
        eventChangeRequest,
        onChangeContextualLinks,
        onSave,
        onSendToReview,
        onUpdateChangeRequest,
        onChangeDueDate,
        onChangeDecorationType,
    } = props;
    const changeRequestInfo = useAppSelector(getChangeRequestInfo)?.[changeRequestId || 0];
    const userEmail = useUserEmail();
    const navigate = useNavigate();
    const {setMode} = useAiTour();

    useEffect(() => {
        setMode(mode);
    }, [mode, setMode]);

    const onClick = () => {
        if (mode === EventCreationMode.create) {
            navigate('/');
        } else {
            const eventCodeRoutePath = createEventRoutePath({eventId: updateEventInfo.id ?? 0});
            navigate(eventCodeRoutePath);
        }
    };

    return (
        <>
            <PageHeadline caption={`Event ${EVENT_MODE_TITLE[mode]} Form`}>
                <ChangeRequestSubscriptionDropdown changeRequestId={changeRequestId}/>
            </PageHeadline>
            <Group direction="v" gap="m">
                <Item style={{width: '100%'}}>
                    <HeaderBlockController
                        eventCodeInfo={updateEventInfo}
                        eventChangeRequest={eventChangeRequest}
                        defaultDueDate={defaultDueDate}
                        mode={mode}
                        validationHighLight={validationHighLight}
                        changeRequestId={changeRequestId}
                        onUpdateChangeRequest={onUpdateChangeRequest}
                        onChangeDueDate={onChangeDueDate}
                        onChangeDecorationType={onChangeDecorationType}
                        onChangeContextualLinks={onChangeContextualLinks}
                    />
                </Item>
                <Item style={{width: '100%'}}>
                    <Group gap="l" alignItems="start">
                        <Item style={{width: '100%'}}>
                            <Group direction="v" gap="xl">
                                <Item style={{width: '100%'}}>
                                    <AttributesBlockController {...{
                                        attributes: updateEventInfo.attributes as CreationAttribute[],
                                        mode,
                                        changeRequestId,
                                        eventChangeRequest,
                                        validationHighLight,
                                        isLoadingAttributes,
                                        decorationTemplate,
                                        template,
                                        isEventMarkedDeprecated,
                                        onUpdateChangeRequest,
                                    }}
                                    />
                                </Item>
                                <Group className="pb-xxxl xls">
                                    <Button onClick={onSendToReview} variant="Type.PRIMARY">Send To Review</Button>
                                    <Button onClick={onSave} variant="Type.PRIMARY">Save</Button>
                                    <Button onClick={onClick} variant="Type.SECONDARY">Discard</Button>
                                    {changeRequestInfo && changeRequestInfo.authorEmail === userEmail ? (
                                        <PushNotification anchorOrigin={{
                                            horizontal: HorizontalPositionElement.RIGHT,
                                            vertical: VerticalPositionElement.BOTTOM,
                                        }}>
                                            <RemoveChangeRequest
                                                changeRequest={changeRequestInfo}
                                                buttonType={BUTTON_TYPE.BUTTON}
                                            />
                                        </PushNotification>
                                    ) : null}
                                    <EventCreateReviewController changeRequestId={changeRequestId}/>
                                </Group>
                            </Group>
                        </Item>
                    </Group>
                </Item>
            </Group>
        </>
    );
};
